import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Save, Sun, Moon, CreditCard, Plus, Trash2, Zap, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import {
  getSettings, saveSettings, getCards, saveCard,
  getTariffs, createTariff, updateTariff, deleteTariff,
  getCregTariffs, saveCregTariff, deleteCregTariff, CREG_REGIONS, price4, fmtDateNl,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const hours = Array.from({ length: 24 }, (_, i) => i);
const hh = (h) => `${String(h).padStart(2, "0")}:00`;

function Section({ title, desc, children }) {
  return (
    <div className="border border-border bg-card rounded-lg p-6 mb-6">
      <h3 className="font-head text-lg font-bold tracking-tight">{title}</h3>
      {desc && <p className="text-sm text-muted-foreground mt-1 mb-5">{desc}</p>}
      <div className={desc ? "" : "mt-5"}>{children}</div>
    </div>
  );
}

export default function Settings() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const { data: cards = [] } = useQuery({ queryKey: ["cards"], queryFn: getCards });
  const [form, setForm] = useState(null);

  useEffect(() => { if (settings && !form) setForm(settings); }, [settings, form]);

  const save = useMutation({
    mutationFn: (d) => saveSettings(d),
    onSuccess: () => { toast.success("Instellingen opgeslagen"); qc.invalidateQueries(); },
    onError: () => toast.error("Opslaan mislukt"),
  });

  const cardMut = useMutation({
    mutationFn: ({ token, data }) => saveCard(token, data),
    onSuccess: () => { toast.success("Laadkaart bijgewerkt"); qc.invalidateQueries(); },
  });

  if (!form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = () => save.mutate({
    currency: form.currency || "EUR",
    expense_label: form.expense_label || "",
    sender_name: form.sender_name || "",
    creg_region: form.creg_region || "flanders",
  });

  return (
    <div>
      <PageHeader subtitle="Configuratie" title="Instellingen" showControls={false} />

      <TariffsSection />

      <Section title="Onkostenvergoeding" desc="Tekst die bovenaan de PDF-onkostenvergoeding verschijnt.">
        <div className="grid grid-cols-1 gap-5">
          <div>
            <Label className="mb-2 block">Omschrijving onkostenvergoeding</Label>
            <Input value={form.expense_label} onChange={(e) => set("expense_label", e.target.value)}
              data-testid="input-expense-label" placeholder="Onkostenvergoeding elektrisch laden" />
          </div>
          <div>
            <Label className="mb-2 block">Afzender / opmerking (optioneel)</Label>
            <Input value={form.sender_name} onChange={(e) => set("sender_name", e.target.value)}
              data-testid="input-sender-name" placeholder="Naam of bedrijf van de afzender" />
          </div>
        </div>
      </Section>

      <Section title="CREG-onkostenvergoeding" desc="Voor de tweede export op basis van het CREG-tarief (terugbetaling thuisladen bedrijfswagen). Kies het gewest van de laadpaal.">
        <div className="max-w-xs">
          <Label className="mb-2 block">Gewest</Label>
          <Select value={form.creg_region || "flanders"} onValueChange={(v) => set("creg_region", v)}>
            <SelectTrigger data-testid="select-creg-region"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(CREG_REGIONS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Section>

      <Button onClick={submit} disabled={save.isPending} className="gap-2 mb-10" data-testid="save-settings-btn">
        <Save className="h-4 w-4" /> Instellingen opslaan
      </Button>

      <Section title="Laadkaarten" desc="Elke laadkaart hoort bij één bedrijf. Naam en zichtbaarheid kunnen aangepast worden.">
        <div className="space-y-3">
          {cards.map((c) => (
            <CardRow key={c.id_token} card={c} onSave={(data) => cardMut.mutate({ token: c.id_token, data })} />
          ))}
        </div>
      </Section>

      <CregTariffsSection region={form.creg_region || "flanders"} />
    </div>
  );
}

const emptyTariff = { valid_from: "", supplier: "", day_price: "", night_price: "", night_start: 22, night_end: 6 };

function TariffsSection() {
  const qc = useQueryClient();
  const { data: tariffs = [], isLoading } = useQuery({ queryKey: ["tariffs"], queryFn: getTariffs });
  const [draft, setDraft] = useState(emptyTariff);

  const invalidate = () => qc.invalidateQueries();

  const createMut = useMutation({
    mutationFn: (d) => createTariff(d),
    onSuccess: () => { toast.success("Tarief toegevoegd"); invalidate(); setDraft(emptyTariff); },
    onError: () => toast.error("Toevoegen mislukt"),
  });
  const delMut = useMutation({
    mutationFn: (id) => deleteTariff(id),
    onSuccess: () => { toast.success("Tarief verwijderd"); invalidate(); },
  });

  const d = draft;
  const setD = (k, v) => setDraft((p) => ({ ...p, [k]: v }));
  const validNew = /^\d{4}-\d{2}-\d{2}$/.test(d.valid_from) && d.day_price !== "" && d.night_price !== "";

  return (
    <Section title="Tarieven per periode"
      desc="Geef je energietarief in met een ingangsdatum en energieleverancier. Bij de berekening wordt per laadsessie automatisch het tarief gebruikt dat op dat moment gold. Prijzen tot 4 cijfers na de komma.">
      <div className="border border-border/70 rounded-md overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-left">
                <th className="px-4 py-2 font-semibold">Vanaf</th>
                <th className="px-4 py-2 font-semibold">Energieleverancier</th>
                <th className="px-4 py-2 font-semibold text-right text-day">Dag €/kWh</th>
                <th className="px-4 py-2 font-semibold text-right text-night">Nacht €/kWh</th>
                <th className="px-4 py-2 font-semibold">Nachtvenster</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Laden…</td></tr>
              )}
              {!isLoading && tariffs.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Nog geen tarief ingesteld.</td></tr>
              )}
              {tariffs.map((t) => (
                <TariffRow key={t.id} t={t} onSaved={invalidate} onDelete={() => delMut.mutate(t.id)} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-3">Nieuw tarief toevoegen</p>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 items-end">
        <div>
          <Label className="mb-1.5 block text-xs">Ingangsdatum</Label>
          <Input type="date" value={d.valid_from} onChange={(e) => setD("valid_from", e.target.value)}
            className="font-mono" data-testid="tariff-input-valid-from" />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">Energieleverancier</Label>
          <Input value={d.supplier} onChange={(e) => setD("supplier", e.target.value)}
            placeholder="bv. Luminus" data-testid="tariff-input-supplier" />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs text-day">Dag €/kWh</Label>
          <Input type="number" step="0.0001" min="0" value={d.day_price} onChange={(e) => setD("day_price", e.target.value)}
            placeholder="0.3500" className="font-mono" data-testid="tariff-input-day-price" />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs text-night">Nacht €/kWh</Label>
          <Input type="number" step="0.0001" min="0" value={d.night_price} onChange={(e) => setD("night_price", e.target.value)}
            placeholder="0.2500" className="font-mono" data-testid="tariff-input-night-price" />
        </div>
        <div className="grid grid-cols-2 gap-1">
          <div>
            <Label className="mb-1.5 block text-xs">Nacht van</Label>
            <Select value={String(d.night_start)} onValueChange={(v) => setD("night_start", parseInt(v, 10))}>
              <SelectTrigger className="font-mono px-2" data-testid="tariff-input-night-start"><SelectValue /></SelectTrigger>
              <SelectContent>{hours.map((h) => <SelectItem key={h} value={String(h)}>{hh(h)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">tot</Label>
            <Select value={String(d.night_end)} onValueChange={(v) => setD("night_end", parseInt(v, 10))}>
              <SelectTrigger className="font-mono px-2" data-testid="tariff-input-night-end"><SelectValue /></SelectTrigger>
              <SelectContent>{hours.map((h) => <SelectItem key={h} value={String(h)}>{hh(h)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <Button
          className="gap-2"
          disabled={!validNew || createMut.isPending}
          onClick={() => createMut.mutate({
            valid_from: d.valid_from, supplier: d.supplier,
            day_price: parseFloat(d.day_price), night_price: parseFloat(d.night_price),
            night_start: d.night_start, night_end: d.night_end,
          })}
          data-testid="tariff-add-btn"
        >
          <Plus className="h-4 w-4" /> Toevoegen
        </Button>
      </div>
    </Section>
  );
}

function TariffRow({ t, onSaved, onDelete }) {
  const [supplier, setSupplier] = useState(t.supplier || "");
  const [day, setDay] = useState(String(t.day_price));
  const [night, setNight] = useState(String(t.night_price));
  const [ns, setNs] = useState(t.night_start);
  const [ne, setNe] = useState(t.night_end);
  const dirty = supplier !== (t.supplier || "") || parseFloat(day) !== t.day_price ||
    parseFloat(night) !== t.night_price || ns !== t.night_start || ne !== t.night_end;

  const mut = useMutation({
    mutationFn: () => updateTariff(t.id, {
      valid_from: t.valid_from, supplier,
      day_price: parseFloat(day), night_price: parseFloat(night), night_start: ns, night_end: ne,
    }),
    onSuccess: () => { toast.success("Tarief bijgewerkt"); onSaved(); },
    onError: () => toast.error("Bijwerken mislukt"),
  });

  return (
    <tr className="border-t border-border/60" data-testid={`tariff-row-${t.id}`}>
      <td className="px-4 py-2 font-mono whitespace-nowrap">{fmtDateNl(t.valid_from)}</td>
      <td className="px-3 py-2">
        <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} className="h-9" placeholder="—" data-testid={`tariff-supplier-${t.id}`} />
      </td>
      <td className="px-3 py-2">
        <Input type="number" step="0.0001" value={day} onChange={(e) => setDay(e.target.value)} className="h-9 font-mono text-right w-28" data-testid={`tariff-day-${t.id}`} />
      </td>
      <td className="px-3 py-2">
        <Input type="number" step="0.0001" value={night} onChange={(e) => setNight(e.target.value)} className="h-9 font-mono text-right w-28" data-testid={`tariff-night-${t.id}`} />
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <Select value={String(ns)} onValueChange={(v) => setNs(parseInt(v, 10))}>
            <SelectTrigger className="h-9 font-mono px-2 w-[74px]"><SelectValue /></SelectTrigger>
            <SelectContent>{hours.map((h) => <SelectItem key={h} value={String(h)}>{hh(h)}</SelectItem>)}</SelectContent>
          </Select>
          <span className="text-muted-foreground text-xs">–</span>
          <Select value={String(ne)} onValueChange={(v) => setNe(parseInt(v, 10))}>
            <SelectTrigger className="h-9 font-mono px-2 w-[74px]"><SelectValue /></SelectTrigger>
            <SelectContent>{hours.map((h) => <SelectItem key={h} value={String(h)}>{hh(h)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-right">
        <Button size="sm" variant="outline" className="mr-2" disabled={!dirty || mut.isPending} onClick={() => mut.mutate()} data-testid={`tariff-save-${t.id}`}>
          Opslaan
        </Button>
        <Button size="sm" variant="ghost" className="text-destructive" onClick={onDelete} data-testid={`tariff-delete-${t.id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}

function CardRow({ card, onSave }) {
  const [name, setName] = useState(card.company_name || "");
  const [active, setActive] = useState(card.active);
  const dirty = name !== card.company_name || active !== card.active;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 border border-border/70 rounded-md p-4" data-testid={`card-row-${card.id_token}`}>
      <CreditCard className="h-5 w-5 text-primary shrink-0" />
      <div className="flex-1 min-w-0">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mb-1" data-testid={`card-name-${card.id_token}`} />
        <div className="text-xs text-muted-foreground font-mono">UID: {card.id_token}</div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Actief</span>
        <Switch checked={active} onCheckedChange={setActive} data-testid={`card-active-${card.id_token}`} />
      </div>
      <Button size="sm" variant="outline" disabled={!dirty} onClick={() => onSave({ company_name: name, active })}
        data-testid={`card-save-${card.id_token}`}>
        Opslaan
      </Button>
    </div>
  );
}

function CregTariffsSection({ region }) {
  const qc = useQueryClient();
  const { data: tariffs = [] } = useQuery({ queryKey: ["creg"], queryFn: getCregTariffs });
  const [q, setQ] = useState("");
  const [fl, setFl] = useState("");
  const [br, setBr] = useState("");
  const [wa, setWa] = useState("");

  const mut = useMutation({
    mutationFn: (data) => saveCregTariff(data),
    onSuccess: () => {
      toast.success("CREG-tarief opgeslagen");
      qc.invalidateQueries({ queryKey: ["creg"] });
      setQ(""); setFl(""); setBr(""); setWa("");
    },
    onError: () => toast.error("Opslaan mislukt"),
  });
  const delMut = useMutation({
    mutationFn: (quarter) => deleteCregTariff(quarter),
    onSuccess: () => { toast.success("CREG-tarief verwijderd"); qc.invalidateQueries({ queryKey: ["creg"] }); },
  });

  const valid = /^\d{4}-Q[1-4]$/.test(q) && fl && br && wa;

  return (
    <Section title="CREG-tarieven per kwartaal" desc="Maximaal vast bedrag (€/kWh) per gewest. Nieuwe kwartalen publiceert de CREG doorlopend — vul ze hier aan. Het actieve gewest is gemarkeerd.">
      <div className="border border-border/70 rounded-md overflow-hidden mb-5">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 text-left">
              <th className="px-4 py-2 font-semibold">Kwartaal</th>
              <th className={`px-4 py-2 font-semibold text-right ${region === "flanders" ? "text-primary" : ""}`}>Vlaanderen</th>
              <th className={`px-4 py-2 font-semibold text-right ${region === "brussels" ? "text-primary" : ""}`}>Brussel</th>
              <th className={`px-4 py-2 font-semibold text-right ${region === "wallonia" ? "text-primary" : ""}`}>Wallonië</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {tariffs.map((t) => (
              <tr key={t.quarter} className="border-t border-border/60" data-testid={`creg-row-${t.quarter}`}>
                <td className="px-4 py-2 font-mono">{t.quarter}</td>
                <td className={`px-4 py-2 font-mono text-right ${region === "flanders" ? "font-semibold text-primary" : ""}`}>{price4(t.flanders)}</td>
                <td className={`px-4 py-2 font-mono text-right ${region === "brussels" ? "font-semibold text-primary" : ""}`}>{price4(t.brussels)}</td>
                <td className={`px-4 py-2 font-mono text-right ${region === "wallonia" ? "font-semibold text-primary" : ""}`}>{price4(t.wallonia)}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => delMut.mutate(t.quarter)} data-testid={`creg-delete-${t.quarter}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-3">Kwartaal toevoegen / bijwerken</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
        <div>
          <Label className="mb-1.5 block text-xs">Kwartaal</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value.toUpperCase())} placeholder="2027-Q1" className="font-mono" data-testid="creg-input-quarter" />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">Vlaanderen</Label>
          <Input type="number" step="0.0001" value={fl} onChange={(e) => setFl(e.target.value)} placeholder="0.3200" className="font-mono" data-testid="creg-input-flanders" />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">Brussel</Label>
          <Input type="number" step="0.0001" value={br} onChange={(e) => setBr(e.target.value)} placeholder="0.3600" className="font-mono" data-testid="creg-input-brussels" />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">Wallonië</Label>
          <Input type="number" step="0.0001" value={wa} onChange={(e) => setWa(e.target.value)} placeholder="0.3700" className="font-mono" data-testid="creg-input-wallonia" />
        </div>
        <Button
          disabled={!valid || mut.isPending}
          onClick={() => mut.mutate({ quarter: q, flanders: parseFloat(fl), brussels: parseFloat(br), wallonia: parseFloat(wa) })}
          data-testid="creg-save-btn"
        >
          Opslaan
        </Button>
      </div>
    </Section>
  );
}
